// Bitácora de visitantes de la clínica. Centro Medico → Clínica → Visitantes.
// Datos personales no sensibles (control de acceso físico, LOPDP: interés legítimo).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DoorOpen, Loader2, LogOut, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { AutoTextarea, Campo, Modal, fieldCls, medRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Visitante {
  id: string; nombre: string; identificacion: string | null; telefono: string | null;
  motivo: string | null; destino: string | null; entrada_at: string; salida_at: string | null; observaciones: string | null;
}
const fmt = (s: string | null) => (s ? new Date(s).toLocaleString(LOCALE, { dateStyle: 'short', timeStyle: 'short' }) : '—');
const vacio = { nombre: '', identificacion: '', telefono: '', motivo: '', destino: '', observaciones: '' };

export function Visitantes() {
  const qc = useQueryClient();
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('');
  const [soloDentro, setSoloDentro] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState(vacio);

  const { data: rows = [], isFetching } = useQuery<Visitante[]>({
    queryKey: ['med-visitantes', filtro, soloDentro],
    queryFn: () => medRpc<Visitante[]>('med_visitantes', { _busqueda: filtro, _solo_dentro: soloDentro }),
  });

  const registrar = useMutation({
    mutationFn: () => medRpc('med_visitante_registrar', { _data: form }),
    onSuccess: () => { toast.success('Visitante registrado'); setAbierto(false); setForm(vacio); void qc.invalidateQueries({ queryKey: ['med-visitantes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const salida = useMutation({
    mutationFn: (id: string) => medRpc('med_visitante_salida', { _id: id }),
    onSuccess: () => { toast.success('Salida registrada'); void qc.invalidateQueries({ queryKey: ['med-visitantes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><DoorOpen className="w-5 h-5 text-primary" /> Visitantes</h1>
        <Button onClick={() => { setForm(vacio); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Registrar visitante</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form className="flex gap-2 max-w-md flex-1" onSubmit={(e) => { e.preventDefault(); setFiltro(busqueda.trim()); }}>
          <input className={fieldCls} placeholder="Buscar por nombre o identificación…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          <Button type="submit" variant="outline"><Search className="w-4 h-4" /></Button>
        </form>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={soloDentro} onChange={(e) => setSoloDentro(e.target.checked)} /> Solo dentro</label>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Visitante</th><th className="py-2 pr-3">Identificación</th><th className="py-2 pr-3">Motivo</th>
              <th className="py-2 pr-3">Destino</th><th className="py-2 pr-3">Entrada</th><th className="py-2 pr-3">Salida</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin visitantes</td></tr>}
              {rows.map((v) => (
                <tr key={v.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{v.nombre}</td>
                  <td className="py-2 pr-3 font-mono text-muted-foreground">{v.identificacion || '—'}</td>
                  <td className="py-2 pr-3">{v.motivo || '—'}</td>
                  <td className="py-2 pr-3">{v.destino || '—'}</td>
                  <td className="py-2 pr-3">{fmt(v.entrada_at)}</td>
                  <td className="py-2 pr-3">{v.salida_at ? fmt(v.salida_at) : <span className="rounded bg-estable-soft px-2 py-0.5 text-xs text-estable">dentro</span>}</td>
                  <td className="py-2 text-right">
                    {!v.salida_at && <Button size="sm" variant="outline" onClick={() => salida.mutate(v.id)}><LogOut className="w-4 h-4 mr-1" /> Salida</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Registrar visitante" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre *" className="col-span-2"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <Campo label="Identificación"><input className={fieldCls} value={form.identificacion} onChange={(e) => setForm({ ...form, identificacion: e.target.value })} /></Campo>
              <Campo label="Teléfono"><input className={fieldCls} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></Campo>
              <Campo label="Motivo"><input className={fieldCls} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} /></Campo>
              <Campo label="Destino (a quién/área)"><input className={fieldCls} value={form.destino} onChange={(e) => setForm({ ...form, destino: e.target.value })} /></Campo>
              <Campo label="Observaciones" className="col-span-2"><AutoTextarea value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></Campo>
            </div>
            <p className="text-xs text-muted-foreground">Se recolectan solo los datos necesarios para el control de acceso (LOPDP: minimización, interés legítimo).</p>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={registrar.isPending} disabled={!form.nombre.trim()} onClick={() => registrar.mutate()}>Registrar entrada</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
